// Centralized API service for MessageCenter
import apiClient from "./buildRequest";
import axios from "axios";
import config from "@/../config";

const WHATSAPP_API_BASE = config.whatsapp_api_url;

/**
 * Message Center API Service
 * Centralizes all API calls for better maintainability and performance
 */
export const messageCenterApi = {
  /**
   * Get DM pages (Facebook/Instagram accounts)
   */
  async getDmPages() {
    try {
      const response = await apiClient.get("/dm/get-dm-pages", {
        // The account picker presents its own actionable error state.
        // Keep this on the config object Axios preserves onto error.config.
        suppressGlobalError: true,
      });
      const pages = response.data?.data;
      return Array.isArray(pages) ? pages : [];
    } catch (error) {
      console.error("❌ Failed to fetch DM pages:", error.response?.data?.msg || error.message);
      throw error;
    }
  },

  /**
   * Get WhatsApp connection status
   */
  async getWhatsAppStatus() {
    try {
      const response = await axios.get(`${WHATSAPP_API_BASE}/whatsapp/status`, {
        timeout: 5000,
      });
      return response.data;
    } catch (error) {
      console.error("❌ Failed to check WhatsApp status:", error.message);
      return { connected: false, info: {}, message: "" };
    }
  },

  async getConversations(accountIds, page = 1, perPage = 20) {
    try {
      const uniqueAccountIds = [...new Set(accountIds)];
      const response = await apiClient.get("/dm/conversations", {
        params: {
          page,
          per_page: Math.min(perPage, 100),
          account_ids: uniqueAccountIds,
        },
      });

      return response.data.data;
    } catch (error) {
      console.error("❌ Error fetching conversations:", error);
      throw error;
    }
  },

  async syncAccounts(accountIds, force = false) {
    const uniqueAccountIds = [...new Set(accountIds)];
    const response = await apiClient.post("/dm/sync", {
      account_ids: uniqueAccountIds,
      force,
    });
    return response.data;
  },

  /**
   * Get messages for a conversation
   * @param {string} conversationId - The conversation ID
   * @param {number} perPage - Number of messages per page
   * @param {string} platform - Optional platform filter (e.g., 'whatsapp', 'messenger', 'instagram')
   */
  async getConversationMessages(conversationId, perPage = 100, platform = null) {
    try {
      const params = { per_page: perPage };
      if (platform) {
        params.platform = platform;
      }
      
      const response = await apiClient.get(`/dm/conversations/${conversationId}/messages`, {
        params,
      });
      return response.data.data[0]?.data || [];
    } catch (error) {
      console.error("❌ Failed to fetch messages:", error);
      throw error;
    }
  },

  /**
   * Get WhatsApp chats with optimized profile picture loading
   */
  async getWhatsAppChats(page = 1, perPage = 30) {
    try {
      const response = await axios.get(`${WHATSAPP_API_BASE}/whatsapp/chats`, {
        params: { page, per_page: perPage },
        timeout: 10000,
      });

      const chats = response.data.data || [];
      
      // Batch load profile pictures only for visible chats (first page)
      if (page === 1) {
        await this.loadProfilePictures(chats);
      }

      return {
        chats,
        pagination: response.data.pagination,
      };
    } catch (error) {
      console.error("❌ Failed to load WhatsApp chats:", error);
      throw error;
    }
  },

  /**
   * Load profile pictures for WhatsApp chats (optimized batch loading)
   */
  async loadProfilePictures(chats) {
    // Only load first 20 profile pictures to avoid too many requests
    const chatsToLoad = chats.slice(0, 20);
    
    const picPromises = chatsToLoad.map(async (chat) => {
      try {
        const response = await axios.get(
          `${WHATSAPP_API_BASE}/whatsapp/profile-pic/${chat.id}`,
          { timeout: 3000 }
        );
        chat.profilePicUrl = response.data.profilePicUrl || null;
      } catch (error) {
        chat.profilePicUrl = null;
      }
    });

    await Promise.all(picPromises);
  },

  /**
   * Get WhatsApp messages for a chat
   */
  async getWhatsAppMessages(chatId, page = 1, perPage = 1250) {
    try {
      const response = await axios.get(
        `${WHATSAPP_API_BASE}/whatsapp/messages/${chatId}`,
        {
          params: { page, per_page: perPage },
          timeout: 15000,
        }
      );
      return response.data.data || [];
    } catch (error) {
      console.error("❌ Failed to load WhatsApp messages:", error);
      throw error;
    }
  },

  /**
   * Upload media file
   */
  async uploadMedia(file) {
    const formData = new FormData();
    formData.append("media", file);
    
    try {
      const response = await apiClient.post("/dm/upload-media", formData);
      return response.data;
    } catch (error) {
      console.error("❌ Upload failed:", error);
      throw error;
    }
  },
};
